import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "oidc-client-ts";
import { getConfig } from "../config";
import { reportError } from "../services/eventBus";
import { setAuthenticatedUser } from "../telemetry/appInsights";
import { getOidcSettings } from "./oidcConfig";
import { getUserManager } from "./userManager";

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * OIDC session against Optimyl.Identity through oidc-client-ts, the same shape as the employer app.
 * When AUTH_ENABLED is false nothing is created: no discovery call, no user, and every route is open.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const authEnabled = getConfig().AUTH_ENABLED === true;
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(authEnabled);
  const userManager = useMemo(() => (authEnabled ? getUserManager() : null), [authEnabled]);

  useEffect(() => {
    if (!userManager) return;

    userManager
      .getUser()
      .then((existing) => {
        if (existing && !existing.expired) {
          setUser(existing);
          if (existing.profile.email) setAuthenticatedUser(existing.profile.email);
        }
      })
      .catch((error: unknown) => reportError(error, "AuthProvider.getUser"))
      .finally(() => setIsLoading(false));

    const onUserLoaded = (loaded: User) => {
      setUser(loaded);
      if (loaded.profile.email) setAuthenticatedUser(loaded.profile.email);
    };
    const onUserUnloaded = () => setUser(null);
    const onSilentRenewError = (error: Error) => {
      reportError(error, "oidc.silentRenew");
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);
    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [userManager]);

  const login = useCallback(async () => {
    if (!userManager) return;
    sessionStorage.setItem("preLoginUrl", window.location.pathname + window.location.search);
    await userManager.signinRedirect();
  }, [userManager]);

  // Direct redirect rather than signoutRedirect(): the library's end-session flow is unreliable on Safari.
  // Redirect before clearing state so the guard does not bounce the user back into sign-in mid-logout.
  const logout = useCallback(async () => {
    const config = getConfig();
    try {
      const current = userManager ? await userManager.getUser() : null;
      const params = new URLSearchParams({ client_id: getOidcSettings().client_id, post_logout_redirect_uri: `${window.location.origin}/logged-out` });
      if (current?.id_token) params.set("id_token_hint", current.id_token);
      window.location.href = `${config.IDENTITY_URL}/connect/logout?${params.toString()}`;
    } catch (error) {
      reportError(error, "AuthProvider.logout");
      window.location.href = `${window.location.origin}/logged-out`;
    }
  }, [userManager]);

  const getAccessToken = useCallback(() => user?.access_token ?? null, [user]);

  const value = useMemo<AuthContextValue>(() => ({ user, isAuthenticated: user !== null && !user.expired, isLoading, login, logout, getAccessToken }), [user, isLoading, login, logout, getAccessToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
